import React, { useEffect, useState } from "react";
import {
  DollarSign, MapPin, Search, Inbox, AlertCircle, ChevronRight,
  Clock, RefreshCw, CheckCircle2, XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchOfferDetails } from "../../analytics/api/analyticsApi";
import PageHeader from "../../../components/shared/PageHeader";

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(num);
};

const STATUS_STYLES = {
  SUBMITTED: { cls: "bg-gray-100 text-gray-600", icon: Clock, label: "Submitted" },
  UNDER_REVIEW: { cls: "bg-yellow-100 text-yellow-700", icon: RefreshCw, label: "Under Review" },
  ACCEPTED: { cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Accepted" },
  REJECTED: { cls: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" },
  COUNTER_OFFER: { cls: "bg-blue-100 text-blue-700", icon: DollarSign, label: "Counter Offer" },
  WITHDRAWN: { cls: "bg-slate-100 text-slate-600", icon: AlertCircle, label: "Withdrawn" },
  EXPIRED: { cls: "bg-orange-100 text-orange-700", icon: AlertCircle, label: "Expired" },
};

const OffersPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const loadOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      const end = new Date().toISOString();
      const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const data = await fetchOfferDetails(start, end, 200);
      setOffers(data || []);
    } catch {
      setError("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const filtered = offers.filter((offer) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const buyerName = `${offer.first_name || ""} ${offer.last_name || ""}`.toLowerCase();
    const email = (offer.email || "").toLowerCase();
    const ref = (offer.reference_number || "").toLowerCase();
    const prop = (offer.property?.address || offer.property?.title || "").toLowerCase();
    return buyerName.includes(q) || email.includes(q) || ref.includes(q) || prop.includes(q);
  });

  const renderPrice = (offer) => {
    if (offer.offer_amount_min != null && offer.offer_amount_max != null) {
      return `${formatMoney(offer.offer_amount_min)} – ${formatMoney(offer.offer_amount_max)}`;
    }
    return formatMoney(offer.offer_amount ?? offer.offer_amount_min ?? offer.offer_amount_max) || "N/A";
  };

  const handleSelectOffer = (offer) => {
    navigate(`/offers/${offer.id}`, { state: { offer } });
  };

  return (
    <div className="w-full min-h-screen bg-gray-50/80">
      <PageHeader
        title="Buyer Portal Offers"
        subtitle="See all offers submitted via the Buyer Portal. Click an offer to view full details."
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Top bar — stats + search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] border border-[#55C5D0]/20">
              <Inbox className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Total Offers</h2>
              <p className="text-xs text-gray-500 font-medium">Last 30 days · Buyer Portal</p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 pl-4 border-l border-gray-200">
              <span className="text-2xl font-extrabold text-[#55C5D0] tabular-nums">{offers.length}</span>
              <span className="text-xs text-gray-500 font-medium">offers</span>
            </div>
          </div>
          <div className="relative w-full sm:w-72 group">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400 group-focus-within:text-[#55C5D0] transition-colors" />
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search buyer, ref, property..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 focus:border-[#55C5D0] focus:bg-white transition-all placeholder:text-gray-400"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-10 h-10 rounded-full border-2 border-[#55C5D0]/30 border-t-[#55C5D0] animate-spin mb-4" />
            <p className="text-sm font-medium text-gray-500">Loading offers...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24">
            <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Inbox className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-gray-700 mb-1">No offers found</p>
            <p className="text-sm text-gray-500 max-w-sm">There are no Buyer Portal offers matching your search.</p>
          </div>
        ) : (
          <>
          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Buyer</th>
                  <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Property</th>
                  <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="text-right py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="w-10 py-4 px-2" aria-hidden="true" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((offer) => {
                  const buyerName = `${offer.first_name || ""} ${offer.last_name || ""}`.trim() || offer.email;
                  const statusStyle = STATUS_STYLES[offer.status] || STATUS_STYLES.SUBMITTED;
                  return (
                    <tr
                      key={offer.id}
                      onClick={() => handleSelectOffer(offer)}
                      className="group bg-white hover:bg-[#55C5D0]/5 transition-colors cursor-pointer"
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] flex items-center justify-center">
                            <DollarSign className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{buyerName}</p>
                            {offer.reference_number && (
                              <p className="text-xs text-gray-500 mt-0.5">Ref: {offer.reference_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5 hidden md:table-cell">
                        {offer.property ? (
                          <p className="text-sm text-gray-600 truncate max-w-[200px] flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            {offer.property.title || offer.property.address}
                          </p>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${statusStyle.cls}`}>
                          <statusStyle.icon className="w-3 h-3" />
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="py-4 px-5 hidden sm:table-cell text-sm text-gray-500">
                        {formatDate(offer.created_at)}
                      </td>
                      <td className="py-4 px-5 text-right">
                        <span className="text-sm font-semibold text-emerald-600">{renderPrice(offer)}</span>
                      </td>
                      <td className="py-4 px-2">
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#55C5D0] transition-colors" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <div className="md:hidden p-4 space-y-3">
          {filtered.map((offer) => {
            const buyerName = `${offer.first_name || ""} ${offer.last_name || ""}`.trim() || offer.email;
            const statusStyle = STATUS_STYLES[offer.status] || STATUS_STYLES.SUBMITTED;
            return (
              <button
                key={offer.id}
                type="button"
                onClick={() => handleSelectOffer(offer)}
                className="w-full text-left rounded-2xl p-4 bg-white border border-gray-100 shadow-sm hover:border-[#55C5D0]/30 hover:shadow-md transition-all flex items-center justify-between gap-3"
              >
                <div className="flex gap-3 min-w-0 flex-1">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] flex items-center justify-center">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{buyerName}</p>
                    {offer.property && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{offer.property.title || offer.property.address}</p>
                    )}
                    <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusStyle.cls}`}>
                      {statusStyle.label}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-sm font-semibold text-emerald-600">{renderPrice(offer)}</span>
                  <ChevronRight className="w-5 h-5 text-gray-300 mt-1" />
                </div>
              </button>
            );
          })}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OffersPage;
